import { Trans, useLingui } from '@lingui/react/macro';
import { useFlag } from '@repo/flags/react';
import { Loader2 } from 'lucide-react';
import { useMemo, useState } from 'react';

import { RotagenApiError } from '@/api/rotagen-api';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DemandControl } from '@/components/build-workspace/demand-control';
import type { DemandForecast } from '@/domain/templates/demand-scaling';
import { bucketIssuesByField, translateErrorCode } from '@/lib/error-utils';
import {
  DEFAULT_FIRST_DAY_OF_WEEK,
  formatDateDMY,
  getDayLabel,
  getEndDayIso,
  getNextWeekStart,
  getWeekBoundaries,
} from '@/lib/date-utils';
import type { RotagenValidationIssue } from '@/api/rotagen-api';
import type { SavedTemplate } from '@/types/saved-template';
import type { Site } from '@/types/template';

/**
 * Single-mode Apply-to-Rota modal (TM1). The custom per-day mode was
 * dropped from v1 per FF11 — the salvaged version sent
 * `templatesByDay`/`templates` payloads that rotagen's strict schema
 * silently stripped, producing one-template-per-week behaviour the
 * user didn't ask for. Re-introduction is gated by `flags.templateBuilder.perDayApply`
 * (already pre-defined per D56) and a backend schema extension; both
 * are out of scope here.
 *
 * Form state:
 * - Site (Select)
 * - Week date (date input — picks any day, computes `weekStart`/`weekEnd`
 *   anchored on the site's `firstDayOfWeek`)
 *
 * Submit emits `{ siteId, dateRange, timeZone }`:
 *
 * - **A11 / D42:** `timeZone` is sourced from the selected site's
 *   `timezone` (populated upstream by `mapSiteResponseToSite`). The
 *   parent forwards it to `createGeneration` so rotagen anchors shift
 *   datetimes to the site's local time.
 * - **A13 / D48:** when `submitError` is a `RotagenApiError` with
 *   `issues`, this modal buckets them via `bucketIssuesByField` and
 *   renders the messages inline next to the offending input
 *   (currently `siteId` / `dateRange`). Validation errors do not fall
 *   through to a toast.
 * - **A14 / D49:** non-field-scoped errors (i.e. no `issues` array or
 *   issues for fields this modal doesn't own) get translated via
 *   `translateErrorCode` and rendered as a modal-level alert.
 */
export function ApplyToRotaModal({
  template,
  sites,
  onSubmit,
  onCancel,
  submitError,
  isLoading = false,
}: ApplyToRotaModalProps) {
  const { t } = useLingui();
  const [isScalingEnabled] = useFlag('TEMPLATE_BUILDER_SCALING');
  const [siteId, setSiteId] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<string>(() => getNextWeekStart());

  // Demand forecast (D56): only when the scaling flag is on and this template
  // scales to demand. A what-if over the template's metric, pre-filled to the
  // baseline (so an untouched forecast generates at base counts). `undefined`
  // forecast values fall back to the baseline downstream.
  const demandScaling = template.demandScaling;
  const showDemand = isScalingEnabled && Boolean(demandScaling?.enabled);
  const [forecast, setForecast] = useState<DemandForecast>({});

  const selectedSite = useMemo(() => sites.find((s) => s.id === siteId), [sites, siteId]);
  const firstDayOfWeek = selectedSite?.settings?.firstDayOfWeek ?? DEFAULT_FIRST_DAY_OF_WEEK;
  const weekBoundaries = getWeekBoundaries(selectedDate, firstDayOfWeek);

  // Scope site list to the template's `scope.sites` when set — otherwise show
  // every site the user can pick from.
  const scopeSiteIds = template.scope.sites?.map((site) => site.id) ?? [];
  const availableSites =
    scopeSiteIds.length > 0 ? sites.filter((site) => scopeSiteIds.includes(site.id)) : sites;

  // Resolve the rare-path fallback string here (where the macro is in
  // scope) and pass it into the helper as plain text — same pattern as
  // `formatCommitError` in `rota-proposal-preview.tsx`.
  const fallbackMessage = t`Something went wrong.`;
  const { fieldIssues, modalLevelError } = useMemo(
    () => splitSubmitError(submitError, fallbackMessage),
    [submitError, fallbackMessage],
  );

  const handleSubmit = () => {
    if (!siteId || !weekBoundaries || !selectedSite) return;
    const timeZone = selectedSite.timezone;
    if (!timeZone) return; // A11: timezone is required; the Submit button is also disabled in this state.
    onSubmit({
      siteId,
      dateRange: `${weekBoundaries.weekStart}/${weekBoundaries.weekEnd}`,
      timeZone,
      ...(showDemand ? { forecast } : {}),
    });
  };

  const isValid = Boolean(siteId && weekBoundaries && selectedSite?.timezone);

  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            <Trans>Apply template to rota</Trans>
          </DialogTitle>
          <DialogDescription>
            <Trans>
              Apply &ldquo;{template.name}&rdquo; to generate a rota for a specific site and week.
            </Trans>
          </DialogDescription>
        </DialogHeader>

        {modalLevelError && (
          <div
            role="alert"
            className="border-destructive/50 bg-destructive/5 text-destructive rounded-md border p-3 text-sm"
          >
            {modalLevelError}
          </div>
        )}

        <div className="space-y-6 py-4">
          <div className="space-y-2">
            <label htmlFor="apply-to-rota-site" className="text-sm font-medium">
              <Trans>Site</Trans> <span className="text-destructive">*</span>
            </label>
            <Select value={siteId} onValueChange={setSiteId} disabled={isLoading}>
              <SelectTrigger id="apply-to-rota-site" className="w-full">
                <SelectValue placeholder={t`Select a site`} />
              </SelectTrigger>
              <SelectContent>
                {availableSites.map((site) => (
                  <SelectItem key={site.id} value={site.id}>
                    {site.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {fieldIssues.get('siteId')?.map((issue, i) => (
              <p key={i} className="text-destructive text-sm" data-testid="field-error-siteId">
                {issue.message}
              </p>
            ))}
          </div>

          <div className="space-y-2">
            <label htmlFor="apply-to-rota-date" className="text-sm font-medium">
              <Trans>Week</Trans> <span className="text-destructive">*</span>
            </label>
            <Input
              id="apply-to-rota-date"
              type="date"
              value={weekBoundaries ? weekBoundaries.weekStart : selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              placeholder={t`Pick any date`}
              disabled={isLoading}
            />
            <p className="text-muted-foreground text-sm">
              <Trans>
                Pick any date and the system will determine the week ({getDayLabel(firstDayOfWeek)}–
                {getDayLabel(getEndDayIso(firstDayOfWeek))}).
              </Trans>
            </p>
            {weekBoundaries && (
              <p className="text-muted-foreground text-sm">
                <Trans>
                  Week: {formatDateDMY(weekBoundaries.weekStart)} –{' '}
                  {formatDateDMY(weekBoundaries.weekEnd)}
                </Trans>
              </p>
            )}
            {siteId && firstDayOfWeek !== DEFAULT_FIRST_DAY_OF_WEEK && (
              <p className="text-muted-foreground text-sm">
                <Trans>
                  First day of week: {getDayLabel(firstDayOfWeek)} (based on Rota settings)
                </Trans>
              </p>
            )}
            {fieldIssues.get('dateRange')?.map((issue, i) => (
              <p key={i} className="text-destructive text-sm" data-testid="field-error-dateRange">
                {issue.message}
              </p>
            ))}
          </div>

          {showDemand && demandScaling && (
            <div className="space-y-2">
              <label className="text-sm font-medium">
                <Trans>Forecast demand</Trans>
              </label>
              <p className="text-muted-foreground text-sm">
                <Trans>
                  Adjust the expected demand for this week — staffing on demand-scaled shifts flexes
                  to match.
                </Trans>
              </p>
              <DemandControl
                scaling={demandScaling}
                forecast={forecast}
                onForecastChange={setForecast}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={isLoading}>
            <Trans>Cancel</Trans>
          </Button>
          <Button onClick={handleSubmit} disabled={!isValid || isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                <Trans>Generating…</Trans>
              </>
            ) : (
              <Trans>Generate rota preview</Trans>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Decomposes a `submitError` into field-bucketed issues (A13) and a
 * single modal-level message (A14). Field paths this modal owns —
 * `siteId` and `dateRange` — go into the bucket map. Anything else
 * (non-validation errors, or validation issues against fields this
 * modal doesn't expose) falls back to a translated modal-level alert.
 * The `fallbackMessage` argument is the last-resort copy used for
 * thrown values that are neither `Error` nor `string`; the caller
 * resolves it through the Lingui macro and passes it in as plain text.
 */
function splitSubmitError(
  error: unknown,
  fallbackMessage: string,
): {
  fieldIssues: Map<string, RotagenValidationIssue[]>;
  modalLevelError: string | null;
} {
  if (!error) {
    return { fieldIssues: new Map(), modalLevelError: null };
  }
  if (error instanceof RotagenApiError && error.issues.length > 0) {
    const allBucketed = bucketIssuesByField(error.issues);
    const ownedFieldIssues = new Map<string, RotagenValidationIssue[]>();
    const unownedIssues: RotagenValidationIssue[] = [];
    for (const [field, issues] of allBucketed) {
      if (OWNED_FIELDS.has(field)) {
        ownedFieldIssues.set(field, issues);
      } else {
        unownedIssues.push(...issues);
      }
    }
    // For unowned validation issues, fall back to the issue's OWN
    // message (most specific user-facing copy) rather than the top
    // level wrapper like "Validation failed".
    const modalLevelError =
      unownedIssues.length > 0
        ? translateErrorCode(unownedIssues[0].code, unownedIssues[0].message)
        : null;
    return { fieldIssues: ownedFieldIssues, modalLevelError };
  }
  if (error instanceof RotagenApiError) {
    return {
      fieldIssues: new Map(),
      modalLevelError: translateErrorCode(error.code, error.message),
    };
  }
  if (error instanceof Error) {
    return { fieldIssues: new Map(), modalLevelError: error.message };
  }
  return { fieldIssues: new Map(), modalLevelError: fallbackMessage };
}

/**
 * Set of field paths this modal owns inputs for. Validation issues
 * against these paths render inline; everything else falls through to
 * the modal-level alert.
 */
const OWNED_FIELDS = new Set<string>(['siteId', 'dateRange']);

/** Submission payload — A11 requires `timeZone` to be non-empty. */
export interface SingleModeSubmitData {
  siteId: string;
  dateRange: string;
  timeZone: string;
  /**
   * What-if demand for the week (D56), present only for demand-scaling
   * templates when the scaling flag is on. The parent maps it to the
   * rotagen `Forecast` keyed by the period's ISO dates.
   */
  forecast?: DemandForecast;
}

interface ApplyToRotaModalProps {
  template: SavedTemplate;
  sites: Site[];
  onSubmit: (data: SingleModeSubmitData) => void;
  onCancel: () => void;
  /** Last submit error from the parent's mutation. A13 buckets validation issues; A14 surfaces non-field errors. */
  submitError?: unknown;
  /** Whether a rota-generation request is in flight. Disables form + Submit, shows "Generating…". */
  isLoading?: boolean;
}
